import { memberAuthService } from '../clients/ApiClients';
import { jwtDecode } from 'jwt-decode';

/**
 * What is actually inside a member access token.
 *
 * This is `MemberClaims` in
 * `back-end-rs/src/api/v1/member_portal/auth/models.rs`, and it is
 * **not** the management `AuthUser` claim set. There is no `email`, no
 * `full_name` and no `user_type` here — decoding those yields
 * `undefined`, which is how the sidebar came to render a blank name and
 * the dashboard a permanent spinner.
 *
 * Identity for display comes from `GET /member-portal/me`, loaded once
 * by `MemberContext`. This module only reads what the token really
 * carries: who the member is and what they may ask about.
 */
interface MemberClaims {
  /** Member id, e.g. `mbr_abc123`. A string — never `parseInt` it. */
  sub?: string;
  exp: number;
  iat?: number;
  token_type?: 'access' | 'refresh';
  home_ids?: string[];
  hoa_ids?: string[];
  company_id?: string;
  is_primary?: boolean;
  token_version?: number;
  /**
   * The management user viewing this account, when the session was opened
   * by impersonation rather than by the homeowner signing in. Absent on
   * every ordinary session.
   *
   * `act` is the RFC 8693 name for the actor in a delegated session. The
   * backend refuses every mutating request on a session carrying it, so
   * this is only ever read to *show* that the session is a view — never
   * to decide whether something is allowed.
   */
  act?: string;
}

const readStored = (key: string): string | null => {
  const value = localStorage.getItem(key) ?? sessionStorage.getItem(key);
  if (value === null || value === '' || value === 'undefined') {
    return null;
  }
  return value;
};

export const getToken = (): string | null => readStored('token');

export const getRefreshToken = (): string | null => readStored('refreshToken');

export const getUserName = (): string | null => readStored('username');

export const getRememberMe = (): string | null => {
  const value = readStored('rememberme');
  return value === 'false' ? null : value;
};

// ============================================================================
// Token change notification
// ============================================================================

/**
 * Subscribers notified whenever the stored token changes.
 *
 * Storage is not reactive, so `MemberProvider` had no way to learn that
 * somebody signed in or out. It fetched the profile once at mount and kept
 * it, which meant signing out and back in as a different member left the
 * previous member's name and homes on screen.
 */
type TokenListener = () => void;

const tokenListeners = new Set<TokenListener>();

const notifyTokenChange = (): void => {
  tokenListeners.forEach((listener) => listener());
};

/**
 * Subscribe to token changes, for `useSyncExternalStore`.
 *
 * Also listens for the `storage` event so signing out in one tab takes
 * effect in the others.
 */
export const subscribeToToken = (listener: TokenListener): (() => void) => {
  tokenListeners.add(listener);

  const onStorage = (event: StorageEvent): void => {
    // `key === null` is a `localStorage.clear()` from another tab.
    if (event.key === null || event.key === 'token') {
      listener();
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    tokenListeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const clearToken = (): void => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  notifyTokenChange();
};

export const clearRefreshToken = (): void => {
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('refreshToken');
};

export const clearUserName = (): void => {
  localStorage.removeItem('username');
  sessionStorage.removeItem('username');
};

export const clearRememberMe = (): void => {
  localStorage.removeItem('rememberme');
  sessionStorage.removeItem('rememberme');
};

export const clearSessionStorage = (): void => {
  sessionStorage.clear();
  notifyTokenChange();
};

export const clearAllTokens = (): void => {
  clearToken();
  clearRefreshToken();
};

export const clearAll = (): void => {
  clearAllTokens();
  clearUserName();
  clearRememberMe();
  clearSessionStorage();
};

export const setToken = (token: string, isRememberMe: boolean): void => {
  if (isRememberMe) {
    localStorage.setItem('token', token);
  } else {
    sessionStorage.setItem('token', token);
  }
  notifyTokenChange();
};

export const setRefreshToken = (
  refreshToken: string,
  isRememberMe: boolean
): void => {
  if (isRememberMe) {
    localStorage.setItem('refreshToken', refreshToken);
  } else {
    sessionStorage.setItem('refreshToken', refreshToken);
  }
};

/**
 * Exchange a refresh token for a new access token.
 *
 * **The backend rotates the refresh token too.** `member_refresh_token`
 * calls `create_token_pair` and returns a fresh `refresh_token` beside
 * the access token. Storing only the access token — which this module
 * used to do — leaves the original refresh token in place until it
 * expires, at which point an actively-used session dies for no visible
 * reason. Persist both.
 */
/**
 * The refresh currently in flight, if any.
 *
 * **Without this, rotation signs everybody out roughly hourly.** The
 * request interceptor in `ApiClients` calls `refreshAccessToken` once per
 * request, so a screen that fires several calls against an expired access
 * token sends several refreshes carrying the *same* refresh token. That
 * was harmless while the old token stayed valid; now that the backend
 * rotates on use, all but the first are replays, and a replay outside the
 * server's grace window revokes the whole session family.
 *
 * Concurrent callers therefore share one request and one answer, and the
 * promise is cleared when it settles so the next refresh starts fresh.
 *
 * Identical to front-porch's guard, deliberately — the two apps share
 * this shape and have twice diverged in it, most recently over persisting
 * the rotated token at all. It does not span tabs; that case is the
 * server's grace window, which is the authority either way.
 */
let inFlightRefresh: Promise<string | null> | null = null;

export const refreshAccessToken = async (
  refreshToken: string
): Promise<string | null> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = (async (): Promise<string | null> => {
    try {
      const response = await memberAuthService.memberRefreshToken({
        memberRefreshRequest: { refresh_token: refreshToken },
      });
      const rememberMe = !!getRememberMe();
      if (response.data.refresh_token) {
        setRefreshToken(response.data.refresh_token, rememberMe);
      }
      return response.data.access_token ?? null;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  })();

  try {
    return await inFlightRefresh;
  } finally {
    inFlightRefresh = null;
  }
};

/** True when the token's `exp` is still in the future. */
const isUnexpired = (token: string): boolean => {
  const decoded = jwtDecode<MemberClaims>(token);
  return decoded.exp > Math.floor(Date.now() / 1000);
};

/**
 * Ask the server whether a token is good.
 *
 * Uses the **member** verify endpoint. The management `/auth/verify`
 * cannot read a member token and rejected every session this app ever
 * had.
 */
export const verifyToken = async (token: string | null): Promise<boolean> => {
  try {
    if (token === null) {
      return false;
    }

    if (!isUnexpired(token)) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const newToken = await refreshAccessToken(refreshToken);
        if (newToken) {
          setToken(newToken, !!getRememberMe());
          return true;
        }
      }
      return false;
    }

    const response = await memberAuthService.memberVerifyToken({
      memberVerifyRequest: { token },
    });
    return response.data.valid;
  } catch (error) {
    console.error('Token verification failed:', error);
    return false;
  }
};

/**
 * Local expiry check, refreshing in place when it has lapsed.
 *
 * Called by the request interceptor before every authenticated call, so
 * it deliberately does no network round-trip in the common case.
 */
export const tokenIsUnexpired = async (
  token: string | null
): Promise<boolean> => {
  try {
    if (token === null) {
      return false;
    }

    if (!isUnexpired(token)) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const newToken = await refreshAccessToken(refreshToken);
        if (newToken) {
          setToken(newToken, !!getRememberMe());
          return true;
        }
      }
      return false;
    }

    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

const decodeToken = (): MemberClaims | null => {
  const token = getToken();
  if (!token) return null;

  try {
    return jwtDecode<MemberClaims>(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * The member's id, e.g. `mbr_abc123`.
 *
 * A **string**. The previous version returned
 * `parseInt(decoded.sub, 10)`, which is `NaN` for every real id — and
 * `Dashboard` treated the falsy result as "still loading" and rendered a
 * spinner forever.
 */
export const getMemberIdFromToken = (): string | null =>
  decodeToken()?.sub ?? null;

/** The homes this member may read. Empty is legitimate, not an error. */
export const getHomeIdsFromToken = (): string[] =>
  decodeToken()?.home_ids ?? [];

/** The HOAs this member belongs to, derived from their homes. */
export const getHoaIdsFromToken = (): string[] => decodeToken()?.hoa_ids ?? [];

export const getCompanyIdFromToken = (): string | null =>
  decodeToken()?.company_id ?? null;

/**
 * The management user viewing this account, or `null` on an ordinary
 * session.
 *
 * **Display only.** The server already refuses every write on an
 * impersonated session, in `member_auth::authenticate`, before a handler
 * runs. Hiding buttons because of this value is a courtesy to the person
 * looking; it is not what makes the session safe, and a UI that forgot to
 * check it would still be unable to change anything.
 */
export const getImpersonatorFromToken = (): string | null =>
  decodeToken()?.act ?? null;

/** Whether this member is the primary contact for their home(s). */
export const getIsPrimaryFromToken = (): boolean =>
  decodeToken()?.is_primary ?? false;

export const generateInitials = (name: string): string => {
  const names = name.split(' ').filter((n) => n.length > 0);
  if (names.length === 0) return '';
  if (names.length === 1) {
    return names[0].length > 1
      ? `${names[0][0]}${names[0][names[0].length - 1]}`.toUpperCase()
      : names[0][0].toUpperCase();
  }
  return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
};
